import { api } from '@goaltree/backend/convex/_generated/api'
import { env } from '@goaltree/env/web'
import { useMutation } from 'convex/react'
import { useCallback, useEffect, useState } from 'react'

const VAPID_PUBLIC_KEY = env.VITE_VAPID_PUBLIC_KEY

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)))
}

export type PushPermission = 'default' | 'granted' | 'denied'

export function useWebPush() {
  const [permission, setPermission] = useState<PushPermission>('default')
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const saveSubscription = useMutation(api.webPush.saveSubscription)
  const removeSubscription = useMutation(api.webPush.removeSubscription)

  const supported =
    typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window

  useEffect(() => {
    if (!supported) return
    const currentPermission = Notification.permission as PushPermission
    setPermission(currentPermission)
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription()
      if (sub) {
        setIsSubscribed(true)
        return
      }
      // Auto-subscribe in PWA/new context when permission was already granted elsewhere
      if (currentPermission === 'granted' && VAPID_PUBLIC_KEY) {
        try {
          const newSub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as unknown as ArrayBuffer,
          })
          const json = newSub.toJSON()
          await saveSubscription({
            endpoint: newSub.endpoint,
            p256dh: json.keys?.p256dh ?? '',
            auth: json.keys?.auth ?? '',
          })
          setIsSubscribed(true)
        } catch {
          // Silent — don't disrupt the app if auto-subscribe fails
        }
      }
    })
  }, [supported, saveSubscription])

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!supported || !VAPID_PUBLIC_KEY) return false
    setIsLoading(true)
    try {
      const reg = await navigator.serviceWorker.ready
      const permission = await Notification.requestPermission()
      setPermission(permission as PushPermission)
      if (permission !== 'granted') return false

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as unknown as ArrayBuffer,
      })

      const json = sub.toJSON()
      await saveSubscription({
        endpoint: sub.endpoint,
        p256dh: json.keys?.p256dh ?? '',
        auth: json.keys?.auth ?? '',
      })
      setIsSubscribed(true)
      return true
    } catch {
      return false
    } finally {
      setIsLoading(false)
    }
  }, [supported, saveSubscription])

  const unsubscribe = useCallback(async () => {
    if (!supported) return
    setIsLoading(true)
    try {
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      if (sub) {
        await removeSubscription({ endpoint: sub.endpoint })
        await sub.unsubscribe()
      }
      setIsSubscribed(false)
    } finally {
      setIsLoading(false)
    }
  }, [supported, removeSubscription])

  return { supported, permission, isSubscribed, isLoading, subscribe, unsubscribe }
}
